import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FacebookPageAnalyticsPostsAnalytics.graphql';
import { Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs } from '../../__generated__/globalTypes';

export const useFacebookPageAnalyticsPostsAnalyticsQuery = (
  options?: QueryHookOptions<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFacebookPageAnalyticsPostsAnalyticsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>
): QueryResult<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs> & {
  getFacebookPageAnalyticsPostsAnalytics: LazyQueryExecFunction<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFacebookPageAnalyticsPostsAnalytics, result] = useLazyQuery<
    Query,
    QueryfacebookPageAnalyticsPostsAnalyticsArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getFacebookPageAnalyticsPostsAnalytics, ...result };
};

export const useFacebookPageAnalyticsPostsAnalyticsPromiseQuery = () => {
  const client = useApolloClient();

  const getFacebookPageAnalyticsPostsAnalytics = useCallback(
    (options?: Omit<QueryOptions<QueryfacebookPageAnalyticsPostsAnalyticsArgs, Query>, 'query'>) =>
      client.query<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFacebookPageAnalyticsPostsAnalytics };
};
// Suspense Query Hooks
export const useFacebookPageAnalyticsPostsAnalyticsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfacebookPageAnalyticsPostsAnalyticsArgs>(QUERY, {
    ...options
  });

  return result;
};
